import { isValid, parse, parseISO } from 'date-fns';
import { get, uniqBy } from 'lodash';
import type { Activity } from 'sr-types/lib/search/v1/graphql';
import { TIME_CONSTANTS } from '../common/constants/constants';
import { APPLICATION_NAMES } from '../common/nav/constants';
import type { DateRange } from '../common/utils/dateTime';
import { API_TIME_FORMAT, mergeDateAndTime, toDateTimeISOWithoutOffset } from '../common/utils/dateTimeValues';
import type { ActivityEntityFormType } from '../production/helpers/activityUtils';
import { END_OF_DAY } from './schedulerConstants';
import type { SchedulerLineItem } from './types';

export const DURATION_UNIT_MAPPING = new Map([
    ['hour', 'h'],
    ['day', 'd'],
    ['week', 'w'],
    ['month', 'M'],
    ['year', 'y']
]);

export const SR_TYPES = {
    Activity: 'Activity',
    Reservation: 'Reservation',
    Resource: 'Resource',
    Production: 'Production'
};

export const ORDERED_FIELD_TYPES = {
    DIRECT: 'direct',
    ARRAY_OBJECT: 'arrayObject',
    COMPOSITE: 'composite',
    ARRAY_INDEX: 'arrayIndex'
} as const;

type OrderedField =
    | { type: typeof ORDERED_FIELD_TYPES.DIRECT; path: string }
    | { type: typeof ORDERED_FIELD_TYPES.ARRAY_OBJECT; path: string }
    | { type: typeof ORDERED_FIELD_TYPES.COMPOSITE; fields: string[]; joinWith?: string }
    | { type: typeof ORDERED_FIELD_TYPES.ARRAY_INDEX; path: string };

export interface FieldPaths {
    orderedFields: OrderedField[];
}

type ReservationResourceId = string;

type ReservationTimelineResourceReference = {
    id?: ReservationResourceId | null;
    label?: string | null;
    name?: string | null;
};

type ReservationTimelineResource = ReservationTimelineResourceReference & {
    reference?: ReservationTimelineResourceReference | null;
};

type ReservationWithTimelineResources = {
    associatedResources?: ReservationTimelineResource[] | null;
    reservationFor?: ReservationTimelineResource | null;
    reservationForName?: string | null;
};

const getReservationResourceId = (resource?: ReservationTimelineResource | null): ReservationResourceId | undefined => {
    const id = (resource?.reference || resource)?.id;
    return id ?? undefined;
};

export const getEventAttributes = (event) => {
    const attributes = {
        name: '',
        resourceLabel: '',
        contact: '',
        organization: '',
        opportunityId: undefined,
        showTime: false,
        opportunityName: '',
        reservationForName: '',
        lineItemId: '',
        production: '',
        organizationName: '',
        status: '',
        opportunityType: '',
        reservationGuestsAvailable: false,
        isChild: true,
        primary: false,
        ownerOrganizationName: '',
        groupType: '',
        parent: '',
        shouldSequenceSchedule: false
    };
    attributes.production = event.production?.name;
    attributes.organizationName = event.organization?.name;
    attributes.resourceLabel = event.status;

    return attributes;
};

export const toSchedulerActivityItemsFromProduction = (
    activity: Activity | ActivityEntityFormType,
    referenceDate: Date
) => {
    const dateRange =
        activity.activityType === 'Milestone'
            ? { ...activity.dateRange, durationSpecified: true, dateTimeDuration: { value: 1, unit: 'day' } }
            : activity.dateRange;
    const startTime: Date = parse(dateRange.startTime, API_TIME_FORMAT, referenceDate);
    const endTime: Date = parse(dateRange.endTime, API_TIME_FORMAT, referenceDate);
    const id = get(activity, 'id') || get(activity, 'identity.id');
    const showTime = isValid(startTime) && isValid(endTime);
    const schedulerActivityItem = {
        ...activity,
        dateRange,
        showTime,
        id,
        products: get(activity, 'products'),
        production: get(activity, 'production') || get(activity, 'productionReference'),
        'sr-type': SR_TYPES.Activity,
        resources: get(activity, 'resourceReferences')?.map((ar) => ar.id)
    };
    return schedulerActivityItem;
};

const isManuallyScheduled = (hasDependencies: boolean, isFirstChild: boolean) => {
    if (hasDependencies) {
        return isFirstChild;
    }
    return true;
};

export const toBryntumEventsAndAssignments = (
    schedulerLineItems: SchedulerLineItem[],
    hasDependencies: boolean,
    referenceDate: Date
) => {
    const events = [];
    const assignmentData = new Set<string>();

    let firstChildFound = false;
    for (let idx = 0; idx < schedulerLineItems.length; idx++) {
        const sli = schedulerLineItems[idx];
        const attributes = { ...sli.attributes };
        if (hasDependencies && !firstChildFound && attributes.isChild) {
            attributes.isFirstChild = true;
            firstChildFound = true;
        }
        const { mergedStart, mergedEnd } = getMergedStartEnd(sli.dateRange, referenceDate);
        const startValid = isValid(mergedStart);
        const endValid = isValid(mergedEnd);
        events.push({
            ...sli,
            attributes,
            id: sli.id,
            opportunityId: sli.attributes.opportunityId,
            opportunityType: sli.attributes.opportunityType,
            reservationGuestsAvailable: sli.attributes.reservationGuestsAvailable,
            isChild: sli.attributes.isChild,
            assignedResources: sli.assignedResources,
            manuallyScheduled: isManuallyScheduled(hasDependencies, attributes.isFirstChild),
            startDate: startValid ? toDateTimeISOWithoutOffset(mergedStart) : undefined,
            endDate: endValid ? toDateTimeISOWithoutOffset(mergedEnd) : undefined,
            reservationForName: sli.attributes.reservationForName,
            duration: sli.dateRange.dateTimeDuration?.value,
            durationUnit: DURATION_UNIT_MAPPING.get(sli.dateRange.dateTimeDuration?.unit),
            durationSpecified: sli.dateRange.durationSpecified,
            lineItemId: sli.attributes.lineItemId,
            calendar: sli.calendar,
            'sr-type': SR_TYPES.Reservation
        });
        sli.resources?.forEach((res) => {
            // Using a Set to prevent duplicates during insertion
            assignmentData.add(
                JSON.stringify({
                    resourceId: res,
                    eventId: sli.id,
                    lineItemId: sli.attributes.lineItemId
                })
            );
        });
    }
    // Convert the set back to an array
    const assignments = Array.from(assignmentData).map((item) => JSON.parse(item));
    const timelineChanges = { events, assignments };
    return timelineChanges;
};

export const toBryntumEventsAndAssignmentsForActivity = (lineItems, application, referenceDate: Date) => {
    const events = [];
    const assignments = [];

    lineItems.forEach((lineItem) => {
        const { mergedStart, mergedEnd } = getMergedStartEnd(lineItem.dateRange, referenceDate);
        const startValid = isValid(mergedStart);
        const endValid = isValid(mergedEnd);
        events.push({
            ...lineItem,
            startDate: startValid ? toDateTimeISOWithoutOffset(mergedStart) : '',
            endDate: endValid ? toDateTimeISOWithoutOffset(mergedEnd) : '',
            startTime: lineItem.dateRange?.startTime,
            endTime: lineItem.dateRange?.endTime,
            type: lineItem.activityType,
            manuallyScheduled: true,
            duration: lineItem.dateRange.dateTimeDuration?.value,
            durationUnit: DURATION_UNIT_MAPPING.get(lineItem.dateRange.dateTimeDuration?.unit),
            locationCity: lineItem.location ? lineItem.location.address?.locality : '',
            locationCountry: lineItem.location ? lineItem.location.address?.countryRegion : '',
            'sr-type': SR_TYPES.Activity
        });
        if ([APPLICATION_NAMES.planning, APPLICATION_NAMES.productionProfessional].includes(application?.name)) {
            lineItem.resources?.forEach((res) => {
                assignments.push({
                    resourceId: res,
                    eventId: lineItem.id
                });
            });
            if (lineItem.production?.id) {
                assignments.push({
                    resourceId: lineItem.production.id,
                    eventId: lineItem.id
                });
            } else if (lineItem.productionReference?.id) {
                assignments.push({
                    resourceId: lineItem.productionReference.id,
                    eventId: lineItem.id
                });
            }
        } else if (application?.name === APPLICATION_NAMES.reservationManagement) {
            lineItem.productReferences?.forEach((res) => {
                assignments.push({
                    resourceId: res.id,
                    eventId: lineItem.id
                });
            });
        }
    });

    const timelineChanges = { events, assignments };
    return timelineChanges;
};

const getReservationResourceLabel = (resource?: ReservationTimelineResource | null): string | undefined => {
    const reference = resource?.reference || resource;
    const resourceLabel = resource?.label || reference?.label || reference?.name || undefined;
    return resourceLabel;
};

const getUniqueReservationResources = (
    reservation: ReservationWithTimelineResources
): ReservationTimelineResource[] => {
    const reservationForReference = reservation?.reservationFor?.reference || reservation?.reservationFor;
    const reservationForResource: ReservationTimelineResource | undefined = reservationForReference
        ? {
              reference: reservationForReference,
              label: reservation?.reservationForName || reservation?.reservationFor?.label
          }
        : undefined;
    const reservationResources = [
        ...(reservationForResource ? [reservationForResource] : []),
        ...(reservation?.associatedResources || [])
    ];
    const uniqueReservationResources = uniqBy(
        reservationResources.filter((resource) => getReservationResourceId(resource) !== undefined),
        getReservationResourceId
    );

    return uniqueReservationResources;
};

export const toSchedulerLineItemsFromReservations = (reservations, referenceDate: Date) => {
    const schedulerLineItems = reservations.map((res) => {
        // TODO: generalize the object structure for RM and production planning timeline views. attributes are currently used in RM views only.
        const attributes = getEventAttributes(res);
        attributes.status = res.status;
        attributes.primary = res?.primary;
        attributes.isChild = !res.hasDetailedSchedule;
        attributes.reservationGuestsAvailable = res?.reservationGuestsAvailable;
        attributes.ownerOrganizationName = res?.ownerOrganizationName;
        attributes.lineItemId = res.lineItemId || res.opportunity.context.lineItemReference.id;
        const startTime: Date = parse(res.dateRange.startTime, API_TIME_FORMAT, referenceDate);
        const endTime: Date = parse(res.dateRange.endTime, API_TIME_FORMAT, referenceDate);
        const uniqueReservationResources = getUniqueReservationResources(res);
        const assignedResources = uniqueReservationResources.map((resource) => {
            const assignedResource = {
                id: getReservationResourceId(resource),
                name: getReservationResourceLabel(resource)
            };
            return assignedResource;
        });
        attributes.resourceLabel = res.status;
        if (isValid(startTime) && isValid(endTime)) {
            attributes.showTime = true;
        }

        attributes.reservationForName = res?.reservationForName || res?.reservationFor?.label;
        if (res.opportunity) {
            attributes.opportunityName = res.opportunity.name;
            attributes.opportunityType = res.opportunity.opportunityType ?? res.opportunity.OpportunityType; // For backwards compatibility: the API response should return opportunityType instead of OpportunityType
            attributes.production = res.opportunity.production?.name;
            attributes.organizationName = res.opportunity.organization?.name;
            if (res.opportunity.contacts && res.opportunity.contacts.length) {
                const c = res.opportunity.contacts[0];
                if (c.role) {
                    attributes.contact = `${c.name.firstName} ${c.name.lastName} - ${c.role}`;
                } else {
                    attributes.contact = `${c.name.firstName} ${c.name.lastName}`;
                }
            }
            if (res.opportunity.organization && res.opportunity.organization.name !== '') {
                attributes.organization = res.opportunity.organization.name;
            }
            if (res.opportunity.id) {
                attributes.opportunityId = res.opportunity.id;
            } else if (res.opportunity.context.containerReference && res.opportunity.context.containerReference.id) {
                attributes.opportunityId = res.opportunity.context.containerReference.id;
            }
        }
        const schedulerLineItem = {
            ...res,
            id: res.id || res.identity.id,
            dateRange: res.dateRange,
            resources: uniqueReservationResources.map(getReservationResourceId),
            attributes,
            assignedResources,
            calendar: res.calendar ? res.calendar.id : undefined,
            organization: res.opportunity.organization?.name,
            production: res.opportunity.production?.name,
            opportunityName: res.opportunity.name
        };
        return schedulerLineItem;
    });
    return schedulerLineItems;
};

export const getMergedStartEnd = (dateRange: DateRange, referenceDate: Date) => {
    const fallbackDate = referenceDate;
    const start = dateRange?.start ? parseISO(dateRange.start) : fallbackDate;
    const startTime = dateRange?.startTime ? parse(dateRange.startTime, API_TIME_FORMAT, fallbackDate) : start;
    const end = dateRange?.end ? parseISO(dateRange.end) : start;
    const endTime = dateRange?.endTime ? parse(dateRange.endTime, API_TIME_FORMAT, fallbackDate) : end;

    const safeStart = isValid(start) ? start : fallbackDate;
    const safeStartTime = isValid(startTime) ? startTime : safeStart;
    const safeEnd = isValid(end) ? end : safeStart;
    const safeEndTime = isValid(endTime) ? endTime : safeEnd;

    const mergedStart = mergeDateAndTime(safeStart, safeStartTime);
    const mergedEnd = mergeDateAndTime(safeEnd, safeEndTime);

    const mergedDates = { mergedStart, mergedEnd };
    return mergedDates;
};

export const FIELDS_TO_USE_FOR_BRYNTUM_EVENT: {
    reservation: FieldPaths;
    reservationLocalInput: FieldPaths;
    activityForReservation: FieldPaths;
    activityForProduction: FieldPaths;
} = {
    reservation: {
        orderedFields: [
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'opportunity.name' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'opportunity.opportunityType' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'opportunity.production.productionReference.id' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'opportunity.organization.organizationReference.id' },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.start', 'dateRange.startTime'],
                joinWith: 'T'
            },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.end', 'dateRange.endTime'],
                joinWith: 'T'
            },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'status' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'reservationFor.reference.id' }
        ]
    },
    reservationLocalInput: {
        orderedFields: [
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'name' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'opportunityType' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'production.productionReference.id' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'organization.organizationReference.id' },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['opportunityPeriod.start', 'opportunityPeriod.startTime'],
                joinWith: 'T'
            },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['opportunityPeriod.end', 'opportunityPeriod.endTime'],
                joinWith: 'T'
            },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'status' },
            { type: ORDERED_FIELD_TYPES.ARRAY_INDEX, path: 'lineItems[0].product.reference.id' }
        ]
    },
    activityForReservation: {
        orderedFields: [
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'name' },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.start', 'dateRange.startTime'],
                joinWith: 'T'
            },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.end', 'dateRange.endTime'],
                joinWith: 'T'
            },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'productionReference.id' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'activityType' },
            { type: ORDERED_FIELD_TYPES.ARRAY_OBJECT, path: 'productReferences.id' }
        ]
    },
    activityForProduction: {
        orderedFields: [
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'name' },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.start', 'dateRange.startTime'],
                joinWith: 'T'
            },
            {
                type: ORDERED_FIELD_TYPES.COMPOSITE,
                fields: ['dateRange.end', 'dateRange.endTime'],
                joinWith: 'T'
            },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'activityType' },
            { type: ORDERED_FIELD_TYPES.DIRECT, path: 'activityStatus' }
        ]
    }
};

export const updateEventsForRenderingOnTimeline = (lineItems: SchedulerLineItem[], referenceDate: Date) => {
    const { events, assignments } = toBryntumEventsAndAssignments(lineItems, false, referenceDate);
    const updatedEvents = events.map((event) => {
        const originalDateRange = event.dateRange;
        if (!originalDateRange) {
            const { duration, ...eventWithoutDuration } = event;
            const undatedEvent = {
                ...eventWithoutDuration,
                startDate: '',
                endDate: ''
            };
            return undatedEvent;
        }
        const isHourly = originalDateRange.dateTimeDuration?.unit === TIME_CONSTANTS.hour;
        const endTime = isHourly ? originalDateRange.endTime : END_OF_DAY;
        const { mergedStart, mergedEnd } = getMergedStartEnd({ ...originalDateRange, endTime }, referenceDate);
        const startValid = isValid(mergedStart);
        const endValid = isValid(mergedEnd);
        const startDate = startValid ? toDateTimeISOWithoutOffset(mergedStart) : undefined;
        const endDate = endValid ? toDateTimeISOWithoutOffset(mergedEnd) : undefined;

        const normalizedDateRange = {
            start: originalDateRange.start,
            end: originalDateRange.end,
            startTime: originalDateRange.startTime,
            endTime,
            dateTimeDuration: originalDateRange.dateTimeDuration
                ? { unit: originalDateRange.dateTimeDuration.unit }
                : undefined,
            durationSpecified: originalDateRange.durationSpecified
        };

        const { duration, ...eventWithoutDuration } = event;
        const normalizedEvent = {
            ...eventWithoutDuration,
            dateRange: normalizedDateRange,
            startDate,
            endDate
        };
        return normalizedEvent;
    });
    const timelineChanges = { events: updatedEvents, assignments };
    return timelineChanges;
};
